import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IconType } from 'react-icons'
import {
  PiCaretLeft,
  PiChartLineUp,
  PiDownloadSimple,
  PiSquaresFour,
} from 'react-icons/pi'
import {
  primaryPillButtonClass,
  secondaryPillButtonClass,
} from 'components/FbUI/secondaryPillButton'
import { cn } from 'components/UI/cn'
import { blankFrame } from './builder'
import { REPORTING_PAGE_SHELL } from './page-shell'
import { ReportEditorBody } from './components/ReportEditorBody'
import { SaveDialog, SaveResult } from './components/SaveDialog'
import { csvFilename, downloadCsv, toCsv } from './csv'
import { saveView } from './saved-views'
import { useReportResult } from './use-report-result'
import { ViewConfig } from './view-config'

/**
 * Build-your-own. Two starting points: a *report* (one component) or a
 * *dashboard* (a composition of existing reports, built on its own page).
 *
 * The report editor is the shared ReportEditorBody: pick a "Show as" view and
 * the sidebar controls reshape the underlying ViewConfig, previewed live in
 * whichever view its shape supports (table / chart / ranked / metric).
 */

/** A clickable start-choice card. */
function ChoiceCard({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: IconType
  title: string
  description: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-full flex-col gap-2 rounded-xl border border-line bg-surface p-5 text-left shadow-sm transition-colors hover:border-brand-active"
    >
      <div className="flex size-9 items-center justify-center rounded-lg bg-brand-active/10 text-brand-active">
        <Icon size={18} />
      </div>
      <div className="font-semibold text-fg">{title}</div>
      <div className="text-sm text-fg-secondary">{description}</div>
    </button>
  )
}

export default function BuilderPage() {
  const navigate = useNavigate()
  const [config, setConfig] = useState<ViewConfig | null>(null)
  const [saveOpen, setSaveOpen] = useState(false)
  const { result } = useReportResult(config ?? undefined)

  const reset = () => {
    setConfig(null)
  }

  const handleSaveNew = ({ name, description }: SaveResult) => {
    if (!config) return
    const saved = saveView({ ...config, description }, name)
    navigate(`/reporting/${saved.id}`)
  }

  const handleExport = () => {
    if (config && result)
      downloadCsv(csvFilename(config.name), toCsv(config, result))
  }

  // 1) Start screen — report or dashboard.
  if (config === null) {
    return (
      <div className={cn(REPORTING_PAGE_SHELL, 'gap-6')}>
        <div>
          <Link
            to="/reporting"
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg"
          >
            <PiCaretLeft size={14} /> Reports
          </Link>
          <h1 className="text-2xl font-bold text-fg">Build your own</h1>
          <p className="text-sm text-fg-secondary">
            Start a single report, or compose existing reports into a dashboard.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ChoiceCard
            icon={PiChartLineUp}
            title="Report builder"
            description="Pick a view, choose what to measure or list, then filter — see it update live."
            onClick={() => setConfig({ ...blankFrame(), name: '' })}
          />
          <ChoiceCard
            icon={PiSquaresFour}
            title="Build a dashboard"
            description="Combine existing reports into a dashboard and arrange them."
            onClick={() => navigate('/reporting/dashboards/new')}
          />
        </div>
      </div>
    )
  }

  // 2) Build a report — editable name/description, then the shared editor body
  // ("Show as" + live preview + operations/filters sidebar).
  return (
    <div className={cn(REPORTING_PAGE_SHELL, 'gap-4')}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg"
          >
            <PiCaretLeft size={14} /> Start over
          </button>
          <input
            type="text"
            value={config.name}
            onChange={(e) => setConfig({ ...config, name: e.target.value })}
            placeholder="Untitled report"
            aria-label="Report name"
            className="w-full bg-transparent text-2xl font-bold text-fg outline-none placeholder:text-fg-muted"
          />
          <input
            type="text"
            value={config.description}
            onChange={(e) =>
              setConfig({ ...config, description: e.target.value })
            }
            placeholder="Add a description (optional)"
            aria-label="Report description"
            className="w-full bg-transparent text-sm text-fg-secondary outline-none placeholder:text-fg-muted"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            className={secondaryPillButtonClass}
          >
            <PiDownloadSimple size={16} /> Export CSV
          </button>
          <button
            type="button"
            onClick={() => setSaveOpen(true)}
            className={primaryPillButtonClass}
          >
            Save report…
          </button>
        </div>
      </div>

      {result && (
        <ReportEditorBody
          config={config}
          result={result}
          onChange={setConfig}
        />
      )}

      <SaveDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        kind="report"
        defaultName={config.name.trim() || 'Untitled report'}
        defaultDescription={config.description}
        onSaveNew={handleSaveNew}
      />
    </div>
  )
}
